import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CATEGORIES, SERVICES, TESTIMONIALS, BRANDS, BUSINESS } from "@/lib/constants";
import { ArrowRight, ShieldCheck, Truck, Wrench, Award, Star, MapPin, Phone, Mail } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import { toast } from "sonner";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [best, setBest] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/products?featured=true&limit=8").then((r) => setFeatured(r.data));
    api.get("/products?bestseller=true&limit=8").then((r) => setBest(r.data));
  }, []);

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", contact);
      toast.success("Thanks! We'll get back to you shortly.");
      setContact({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950">
        <img src="https://images.unsplash.com/photo-1497215842964-222b430dc094?auto=format&fit=crop&w=2000&q=80" alt="office IT" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 text-xs uppercase tracking-widest mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              Genuine products · Authorized service
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-balance leading-[1.05]">
              Your Trusted Partner for<br/>
              <span className="text-blue-300">CCTV, Computers & Printers</span>
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              From surveillance to laptops, printers to networking — SSB Copier delivers premium technology, honest pricing, and expert on-site service across Delhi NCR.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/products" className="bg-blue-600 hover:bg-blue-500 text-white px-7 py-4 rounded-xl font-medium inline-flex items-center gap-2 transition hover:-translate-y-0.5 shadow-lg shadow-blue-600/30" data-testid="hero-shop-btn">
                Shop Products <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/services" className="bg-white/10 hover:bg-white/20 backdrop-blur border border-white/30 text-white px-7 py-4 rounded-xl font-medium inline-flex items-center gap-2 transition" data-testid="hero-services-btn">
                Book a Service
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              {[{v:"5000+", l:"Installations"},{v:"1200+", l:"Businesses served"},{v:"15+ yrs", l:"Trusted expertise"}].map((s) => (
                <div key={s.l}>
                  <div className="font-heading text-2xl sm:text-3xl font-bold text-white">{s.v}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-300 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* USPs */}
      <section className="border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: ShieldCheck, t: "100% Genuine", d: "Authorized dealer for top brands" },
            { icon: Truck, t: "Fast Delivery", d: "Same-day dispatch in Delhi NCR" },
            { icon: Wrench, t: "Expert Service", d: "Certified technicians on call" },
            { icon: Award, t: "AMC & Support", d: "Annual maintenance contracts" },
          ].map((u) => (
            <div key={u.t} className="flex gap-4 items-start">
              <div className="w-11 h-11 grid place-items-center rounded-xl bg-blue-50 text-blue-900"><u.icon className="w-5 h-5" /></div>
              <div>
                <div className="font-heading font-semibold text-slate-900">{u.t}</div>
                <div className="text-sm text-slate-500">{u.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-sm uppercase tracking-widest text-blue-700 font-semibold mb-2">Shop by Category</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Featured Categories</h2>
            </div>
            <Link to="/products" className="hidden sm:inline-flex items-center gap-2 text-blue-900 font-medium hover:gap-3 transition-all" data-testid="home-view-all-cats">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {CATEGORIES.slice(0, 8).map((c) => (
              <Link key={c.slug} to={`/products?category=${encodeURIComponent(c.slug)}`} className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-slate-100" data-testid={`cat-tile-${c.slug}`}>
                <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent"></div>
                <div className="absolute inset-0 p-4 flex flex-col justify-end text-white">
                  <div className="font-heading font-semibold text-lg leading-tight">{c.name}</div>
                  <div className="mt-1 text-xs inline-flex items-center gap-1 text-blue-200 opacity-0 group-hover:opacity-100 transition">Explore <ArrowRight className="w-3 h-3" /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Best sellers */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-sm uppercase tracking-widest text-blue-700 font-semibold mb-2">Most Loved</div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Best Selling Products</h2>
            </div>
            <Link to="/products" className="text-blue-900 font-medium">Shop all</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {best.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-sm uppercase tracking-widest text-blue-700 font-semibold mb-2">Our Services</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Expert Support, End to End</h2>
            <p className="mt-4 text-slate-600">Installation, repair, AMC and on-site IT support — handled by our certified in-house technicians.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/services#${s.slug}`} className="group bg-white border border-slate-200 rounded-2xl p-8 transition-all shadow-soft hover:shadow-lift hover:-translate-y-1 hover:border-blue-200" data-testid={`service-card-${s.slug}`}>
                <div className="w-12 h-12 grid place-items-center rounded-xl bg-blue-900 text-white mb-5 group-hover:bg-blue-700 transition">
                  <Wrench className="w-5 h-5" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900 mb-2">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-blue-900 font-medium">Book now <ArrowRight className="w-3.5 h-3.5" /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 md:py-24 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-sm uppercase tracking-widest text-blue-300 font-semibold mb-2">Why Choose Us</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Reliability our customers stake their business on.</h2>
            <p className="mt-4 text-slate-300 leading-relaxed">We combine 15+ years of technical expertise with premium products from authorized brands — plus a service team that actually picks up the phone.</p>
            <ul className="mt-8 space-y-4">
              {["Only authorized, brand-new stock — no grey market.","Certified technicians for every product line.","Transparent quotations and no hidden charges.","Priority AMC & on-site emergency support."].map((t) => (
                <li key={t} className="flex gap-3"><ShieldCheck className="w-5 h-5 text-blue-300 shrink-0 mt-0.5" /> <span className="text-slate-200">{t}</span></li>
              ))}
            </ul>
          </div>
          {featured.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {featured.slice(0, 4).map((p, i) => (
                <Link key={p.product_id} to={`/product/${p.product_id}`} className={`relative rounded-2xl overflow-hidden group aspect-square ${i % 2 ? 'translate-y-8' : ''}`}>
                  <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="text-xs text-blue-200 uppercase tracking-widest">{p.brand}</div>
                    <div className="text-sm font-heading font-semibold line-clamp-2">{p.name}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-sm uppercase tracking-widest text-blue-700 font-semibold mb-2">Client Voices</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">What our customers say</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-soft">
                <div className="flex gap-0.5 text-amber-400 mb-4">{Array.from({length:t.rating}).map((_,x)=><Star key={x} className="w-4 h-4 fill-current"/>)}</div>
                <p className="text-slate-700 leading-relaxed">“{t.text}”</p>
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <div className="font-heading font-semibold text-slate-900">{t.name}</div>
                  <div className="text-sm text-slate-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands */}
      <section className="py-14 border-y border-slate-100 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold text-center mb-6">Trusted brands we deal in</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {BRANDS.map((b) => (
              <div key={b} className="font-heading font-semibold text-slate-500 text-lg hover:text-blue-900 transition">{b}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact + Map */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-sm uppercase tracking-widest text-blue-700 font-semibold mb-2">Get in touch</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Have a question or need a quote?</h2>
            <p className="mt-4 text-slate-600">Tell us what you need — a camera install, a laptop, or an AMC contract. We'll respond within 24 hours.</p>
            <ul className="mt-8 space-y-4 text-slate-700">
              <li className="flex gap-3"><MapPin className="w-5 h-5 text-blue-700 mt-0.5" /> {BUSINESS.address}</li>
              <li className="flex gap-3"><Phone className="w-5 h-5 text-blue-700 mt-0.5" /> {BUSINESS.phone}</li>
              <li className="flex gap-3"><Mail className="w-5 h-5 text-blue-700 mt-0.5" /> {BUSINESS.email}</li>
            </ul>
            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200">
              <iframe title="map" src={BUSINESS.mapEmbed} width="100%" height="260" loading="lazy" className="border-0"></iframe>
            </div>
          </div>
          <form onSubmit={submitContact} className="bg-slate-50 border border-slate-200 rounded-2xl p-8 space-y-4" data-testid="home-contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input required placeholder="Name" value={contact.name} onChange={(e) => setContact({...contact, name: e.target.value})} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" data-testid="home-contact-name" />
              <input required type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({...contact, email: e.target.value})} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" data-testid="home-contact-email" />
            </div>
            <input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({...contact, phone: e.target.value})} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" data-testid="home-contact-phone" />
            <input placeholder="Subject" value={contact.subject} onChange={(e) => setContact({...contact, subject: e.target.value})} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" data-testid="home-contact-subject" />
            <textarea required rows={5} placeholder="Your message..." value={contact.message} onChange={(e) => setContact({...contact, message: e.target.value})} className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" data-testid="home-contact-message"></textarea>
            <button disabled={sending} className="w-full bg-blue-900 hover:bg-blue-800 text-white py-3.5 rounded-xl font-medium transition disabled:opacity-60" data-testid="home-contact-submit">
              {sending ? "Sending..." : "Send Inquiry"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
